import { z } from 'zod';

const coordinateSchema = z.tuple([z.number(), z.number()]);
const elevationPointSchema = z.tuple([
  z.number().nonnegative(),
  z.number(),
  z.number().min(-180).max(180),
  z.number().min(-90).max(90),
]);

export const activityIdSchema = z.string().regex(/^\d+$/);
export const walkDateSchema = z.iso.date();

export const activityPropertiesSchema = z.object({
  activity_id: z.string().min(1),
  date: z.iso.date(),
  title: z.string().min(1),
  distance_km: z.number().nonnegative(),
  elevation_gain_m: z.number().nonnegative(),
  min_elevation_m: z.number().nullable(),
  max_elevation_m: z.number().nullable(),
  elevation_profile: z.array(elevationPointSchema),
  canonical_strava_url: z.url().refine((value) => new URL(value).hostname === 'www.strava.com'),
  source_activity_ids: z.array(z.string().min(1)).optional(),
  canonical_strava_urls: z
    .array(z.url().refine((value) => new URL(value).hostname === 'www.strava.com'))
    .optional(),
});

export const recordedRouteSchema = z.object({
  type: z.literal('FeatureCollection'),
  metadata: z.object({
    title: z.string(),
    routeKind: z.literal('recorded'),
    scope: z.literal('tip-to-tip-japan'),
    start: z.string(),
    finish: z.string(),
    distanceKm: z.number().positive(),
    elevationGainM: z.number().nonnegative(),
    activityCount: z.number().int().positive(),
    started: z.iso.date(),
    finished: z.iso.date(),
    source: z.string(),
    bbox: z.tuple([z.number(), z.number(), z.number(), z.number()]),
  }),
  features: z.array(
    z.object({
      type: z.literal('Feature'),
      id: z.union([z.string(), z.number()]),
      properties: activityPropertiesSchema,
      geometry: z.object({
        type: z.literal('MultiLineString'),
        coordinates: z.array(z.array(coordinateSchema).min(2)),
      }),
    }),
  ),
});

export const photoManifestSchema = z.object({
  generated_at: z.iso.datetime(),
  media: z.array(
    z.object({
      id: z.string().min(1),
      capture_date: z.iso.date(),
      capture_time: z.iso.datetime(),
      lat: z.number().nullable(),
      lon: z.number().nullable(),
      width: z.number().int().positive().nullable(),
      height: z.number().int().positive().nullable(),
      media_type: z.enum(['image', 'video']),
      orientation: z.enum(['square', 'landscape', 'portrait']).nullable(),
      thumbnail_path: z.string().startsWith('/photos/thumbs/'),
      spotlight_path: z.string().startsWith('/photos/spotlight/'),
    }),
  ),
});

export type Activity = z.infer<typeof activityPropertiesSchema>;
export type RecordedRoute = z.infer<typeof recordedRouteSchema>;
export type Photo = z.infer<typeof photoManifestSchema>['media'][number];

export async function fetchAndParse<T>(path: string, schema: z.ZodType<T>): Promise<T> {
  const response = await fetch(path);
  if (!response.ok) throw new Error(`Could not load ${path}.`);
  return schema.parse(await response.json());
}
